import React from "react";
import { Row, Col, Button, Typography } from "antd";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronLeft,
  CreditCard,
  Bell,
  Shield,
  FileText,
  Settings,
  AlertTriangle,
  Scale,
  ScrollText,
} from "lucide-react";
import logoText from "@assets/images/logo/light-logo-text-nobg.png";
import { SpaceBackground } from "@components";

const { Title, Paragraph } = Typography;

const Terms = () => {
  const history = useHistory();

  const sections = [
    {
      icon: FileText,
      title: "Introduction",
      content: `Welcome to Copilotly ("Company", "we", "our", "us"). These Terms of Service ("Terms", "Terms of Service") govern your use of our web services at https://www.copilotly.com and the related subdomains including but not limited to https://app.copilotly.com and https://api.copilotly.com operated by Netraga Pvt. Ltd.

Our Privacy Policy also governs your use of our Service and explains how we collect, safeguard and disclose information that results from your use of our web pages. Your agreement with us includes these Terms and our Privacy Policy ("Agreements").`,
    },
    {
      icon: Bell,
      title: "Communications",
      content:
        "By creating an Account on our Service, you agree to subscribe to newsletters, marketing or promotional materials and other information we may send. However, you may opt out of receiving any, or all, of these communications from us by following the unsubscribe link or by emailing support@copilotly.com",
    },
    {
      icon: CreditCard,
      title: "Purchases & Subscriptions",
      content: `You may be asked to provide payment information for Purchases, including credit card details and billing information. You represent that you have the legal right to use the payment methods provided.

Subscriptions are billed on a recurring basis (monthly or annual). Your Subscription will automatically renew unless cancelled. We reserve the right to modify Subscription fees with prior notice.`,
    },
    {
      icon: Settings,
      title: "Fee Changes & Refunds",
      content: `Copilotly may modify Subscription fees at any time, effective at the end of the current Billing Cycle. Prior notice will be provided.

We offer refunds within thirty (30) days of purchase if you're not satisfied. Contact support@copilotly.com for refund requests.`,
    },
    {
      icon: AlertTriangle,
      title: "Restrictions",
      content:
        "You must comply with all applicable laws, including privacy laws, intellectual property laws, anti-spam laws, and regulatory requirements. Copilotly may not be used for illegal activities or distributing content you don't own.",
    },
    {
      icon: Shield,
      title: "Service Changes & Terms Updates",
      content: `We reserve the right to modify or withdraw our Service at our discretion. We may amend these Terms at any time by posting updated terms. Your continued use of the Service constitutes acceptance of any changes.`,
    },
  ];

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Terms of Service | Copilotly</title>
        <meta
          name="description"
          content="Read Copilotly's Terms of Service to understand your rights and responsibilities when using our AI-powered productivity platform."
        />
        <link rel="canonical" href="https://app.copilotly.com/terms" />
      </Helmet>

      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "#0a0612",
          overflowY: "auto",
        }}
      >
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none" }}>
          <SpaceBackground />
        </div>
        <div
          style={{
            padding: "40px 20px 80px 20px",
            position: "relative",
            minHeight: "100%",
            zIndex: 1,
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              onClick={() => history.goBack()}
              style={{
                position: "fixed",
                top: 20,
                left: 20,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "rgba(167, 139, 250, 0.15)",
                border: "1px solid rgba(167, 139, 250, 0.3)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
                zIndex: 1000,
                color: "#ffffff",
                fontWeight: 500,
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                padding: "8px 16px",
                height: "auto",
              }}
            >
              <ChevronLeft size={18} style={{ color: "#a78bfa" }} />
              <span>Back</span>
            </Button>
          </motion.div>

          <Row justify="center">
            <Col xs={24} sm={24} md={20} lg={16} xl={14}>
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  textAlign: "center",
                  marginBottom: "40px",
                  marginTop: "60px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <img
                  src={logoText}
                  alt="Copilotly"
                  style={{
                    width: "200px",
                    marginBottom: "32px",
                    display: "block",
                  }}
                />

                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "rgba(167, 139, 250, 0.15)",
                    borderRadius: "20px",
                    padding: "8px 16px",
                    marginBottom: "20px",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    border: "1px solid rgba(167, 139, 250, 0.2)",
                  }}
                >
                  <ScrollText size={16} color="#a78bfa" />
                  <span style={{ color: "#c4b5fd", fontSize: "13px", fontWeight: 500 }}>
                    Legal Document
                  </span>
                </div>

                <h1
                  style={{
                    color: "#fff",
                    fontSize: "clamp(32px, 5vw, 48px)",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    margin: "0 0 16px 0",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Terms of{" "}
                  <span
                    style={{
                      background: "linear-gradient(135deg, #a78bfa 0%, #c4b5fd 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Service
                  </span>
                </h1>

                <p
                  style={{
                    color: "rgba(255,255,255,0.5)",
                    fontSize: "14px",
                    margin: 0,
                  }}
                >
                  Last updated: December 2024
                </p>
              </motion.div>

              {/* Intro Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                style={{
                  background: "rgba(167, 139, 250, 0.08)",
                  borderRadius: "20px",
                  padding: "28px",
                  marginBottom: "32px",
                  border: "1px solid rgba(167, 139, 250, 0.15)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "12px",
                      background: "rgba(167, 139, 250, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Scale size={20} color="#a78bfa" />
                  </div>
                  <span style={{ color: "#fff", fontSize: "18px", fontWeight: 600 }}>
                    Agreement Overview
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "15px",
                    color: "rgba(255,255,255,0.7)",
                    margin: 0,
                    lineHeight: 1.7,
                  }}
                >
                  Please read these terms carefully before using Copilotly. By using our service, you agree to be bound by these terms. These terms outline the rules and regulations for using our AI-powered productivity platform.
                </p>
              </motion.div>

              {/* Sections */}
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.15 + index * 0.05 }}
                    style={{
                      background: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "16px",
                      padding: "24px",
                      marginBottom: "16px",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      backdropFilter: "blur(12px)",
                      WebkitBackdropFilter: "blur(12px)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "12px",
                          background: "rgba(167, 139, 250, 0.15)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon size={22} color="#a78bfa" />
                      </div>
                      <h3
                        style={{
                          margin: 0,
                          color: "#ffffff",
                          fontSize: "18px",
                          fontWeight: 600,
                        }}
                      >
                        {section.title}
                      </h3>
                    </div>
                    <p
                      style={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "15px",
                        lineHeight: 1.8,
                        whiteSpace: "pre-line",
                        margin: 0,
                        paddingLeft: "58px",
                      }}
                    >
                      {section.content}
                    </p>
                  </motion.div>
                );
              })}

              {/* Contact Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                style={{
                  background: "linear-gradient(135deg, rgba(167, 139, 250, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)",
                  borderRadius: "20px",
                  padding: "32px",
                  marginTop: "32px",
                  marginBottom: "32px",
                  textAlign: "center",
                  border: "1px solid rgba(167, 139, 250, 0.2)",
                  backdropFilter: "blur(12px)",
                  WebkitBackdropFilter: "blur(12px)",
                }}
              >
                <h4
                  style={{
                    color: "#ffffff",
                    marginBottom: "12px",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  Questions about our Terms?
                </h4>
                <p style={{ color: "rgba(255,255,255,0.6)", marginBottom: "20px", fontSize: "15px" }}>
                  We're here to help clarify any questions you may have.
                </p>
                <a
                  href="mailto:support@copilotly.com"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "rgba(167, 139, 250, 0.2)",
                    border: "1px solid rgba(167, 139, 250, 0.4)",
                    borderRadius: "12px",
                    padding: "12px 24px",
                    color: "#c4b5fd",
                    fontWeight: 500,
                    textDecoration: "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  support@copilotly.com
                </a>
              </motion.div>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default Terms;
